import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

// Always use the current domain for API calls
const API_BASE_URL = window.location.origin;

export default function OrderForm({ user, onOrderPlaced }) {
  const [tip, setTip] = useState('');
  const [status, setStatus] = useState('online');
  const [doordashCarts, setDoordashCarts] = useState([]);
  const [doordashCartPreviews, setDoordashCartPreviews] = useState([]);
  const [doordashReceipt, setDoordashReceipt] = useState(null);
  const [doordashReceiptPreview, setDoordashReceiptPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const navigate = useNavigate();

  // Get and monitor website status
  useEffect(() => {
    // Get initial status
    fetch('/api/status')
      .then(res => res.json())
      .then(data => {
        console.log('Initial status:', data);
        setStatus(data.status);
      })
      .catch(err => console.error('Error fetching status:', err));

    // Connect to WebSocket for status updates
    const socket = io();
    socket.on('status-update', (data) => {
      console.log('Status update received:', data);
      setStatus(data.status);
    });

    return () => socket.disconnect();
  }, []);

  const handleOrder = async () => {
    // Prevent ordering if website is offline
    if (status === 'offline') {
      return;
    }

    setLoading(true);
    
    // 1. Upload DoorDash cart screenshots
    let doordash_cart_urls = [];
    if (doordashCarts.length > 0) {
      console.log('Uploading DoorDash cart screenshots:', doordashCarts.length, 'files');
      for (let i = 0; i < doordashCarts.length; i++) {
        const cart = doordashCarts[i];
        console.log(`Uploading cart screenshot ${i + 1}:`, cart.name);
        // Sanitize filename to remove spaces and special characters
        const sanitizedCartName = cart.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const { data, error } = await supabase.storage
          .from('doordash-carts')
          .upload(`user-${user.id}/cart-${Date.now()}-${i}-${sanitizedCartName}`, cart);
        if (error) {
          console.error('Cart upload error:', error);
          return;
        }
        console.log('Cart upload success:', data);
        doordash_cart_urls.push(data.path);
      }
    }

    // 2. Upload DoorDash receipt screenshot
    let doordash_receipt_url = null;
    if (doordashReceipt) {
      console.log('Uploading DoorDash receipt screenshot:', doordashReceipt.name);
      // Sanitize filename to remove spaces and special characters
      const sanitizedReceiptName = doordashReceipt.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const { data, error } = await supabase.storage
        .from('screenshots')
        .upload(`user-${user.id}/receipt-${Date.now()}-${sanitizedReceiptName}`, doordashReceipt);
      if (error) {
        console.error('Receipt upload error:', error);
        return;
      }
      console.log('Receipt upload success:', data);
      doordash_receipt_url = data.path;
    }

    // Ensure tip is numeric; default to 0 if blank
    const numericTip = parseFloat(tip) || 0;

    // 3. Create order using our API endpoint (includes email confirmation)
let manualTotal = typeof window !== 'undefined' && window.manualOrderTotal ? window.manualOrderTotal : '0.00';
// Store multiple cart URLs as JSON string in the existing doordash_cart_url column
const cartUrlsData = doordash_cart_urls.length > 0 ? JSON.stringify(doordash_cart_urls) : null;

console.log('Creating order with:', {
  user_id: user.id,
  tip: numericTip,
  doordash_cart_url: cartUrlsData,
  doordash_receipt_url,
  status: 'pending_payment',
  total: manualTotal
});

// Create order through our API endpoint (includes email confirmation)
const orderResponse = await fetch(`${API_BASE_URL}/api/create-order`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    user_id: user.id,
    tip: numericTip,
    doordash_cart_url: cartUrlsData,
    doordash_receipt_url,
    status: 'pending_payment',
    total: manualTotal
  })
});

if (!orderResponse.ok) {
  const errorData = await orderResponse.json();
  console.error('Order creation failed:', errorData);
  setPopupMessage('Failed to create order. Please try again.');
  setShowPopup(true);
  setLoading(false);
  return;
}

const { data, error } = await orderResponse.json();
if (error) {
  console.error('Order creation error:', error);
  setPopupMessage('Failed to create order. Please try again.');
  setShowPopup(true);
  setLoading(false);
  return;
}
console.log('Order creation success:', data);
// Call backend to extract financials from receipt
try {
  const response = await fetch(`${API_BASE_URL}/api/extract-financials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      order_id: data.id,
      receipt_path: doordash_receipt_url,
      tip: numericTip
    })
  });
  
  console.log('Response status:', response.status);
  console.log('Response ok:', response.ok);
  
  if (!response.ok) {
    let result;
    try {
      result = await response.json();
      console.log('Error result:', result);
    } catch (parseError) {
      console.error('Failed to parse response:', parseError);
      setPopupMessage('Failed to process receipt. Please try again.');
      setShowPopup(true);
      setLoading(false);
      return;
    }
    
    // Check for subtotal validation errors
    if (response.status === 400 && (result.error === 'Order rejected' || result.error === 'Subtotal does not meet minimum subtotal requirements')) {
      console.log('Subtotal validation error detected');
      // Order was rejected due to subtotal validation
      let message = result.message || result.error;
      if (result.subtotal) {
        message = `Order rejected: Subtotal $${result.subtotal.toFixed(2)} is outside allowed range ($15.00-$20.00)`;
      }
      setPopupMessage(message);
      setShowPopup(true);
      
      // Delete the rejected order from database
      await supabase.from('orders').delete().eq('id', data.id);
      
      setLoading(false);
      return;
    } else {
      console.error('Receipt extraction failed:', result);
      setPopupMessage('Failed to process receipt. Please try again.');
      setShowPopup(true);
      setLoading(false);
      return;
    }
  }
  
  // If response is ok, try to parse the result
  let result;
  try {
    result = await response.json();
  } catch (parseError) {
    console.error('Failed to parse successful response:', parseError);
  }
  
  console.log('Receipt extraction complete');
} catch (err) {
  console.error('Receipt extraction failed:', err);
  setPopupMessage('Failed to process receipt. Please try again.');
  setShowPopup(true);
  setLoading(false);
  return;
}

    
    // Get the order ID from the response
    const { data: newOrder } = await supabase
      .from('orders')
      .select('id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    
    if (newOrder) {
      navigate(`/payment?orderId=${newOrder.id}`);
    } else {
      navigate('/payment');
    }
  };

  const handleDoordashCartChange = (e) => {
    // Prevent file upload if website is offline
    if (status === 'offline') {
      return;
    }
    const files = Array.from(e.target.files);
    setDoordashCarts(files);
    
    // Create previews for all selected files
    const previews = [];
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews[index] = reader.result;
        if (previews.length === files.length) {
          setDoordashCartPreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDoordashReceiptChange = (e) => {
    // Prevent file upload if website is offline
    if (status === 'offline') {
      return;
    }
    const file = e.target.files[0];
    setDoordashReceipt(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setDoordashReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setDoordashReceiptPreview(null);
    }
  };

  // Remove individual cart image
  const removeCartImage = (indexToRemove) => {
    const newCarts = doordashCarts.filter((_, index) => index !== indexToRemove);
    const newPreviews = doordashCartPreviews.filter((_, index) => index !== indexToRemove);
    setDoordashCarts(newCarts);
    setDoordashCartPreviews(newPreviews);
  };

  // Remove receipt image
  const removeReceiptImage = () => {
    setDoordashReceipt(null);
    setDoordashReceiptPreview(null);
    // Clear the file input
    const fileInput = document.getElementById('doordashReceipt');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Clear all cart images
  const clearAllCartImages = () => {
    setDoordashCarts([]);
    setDoordashCartPreviews([]);
    // Clear the file input
    const fileInput = document.getElementById('doordashCart');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const isFormValid = doordashCarts.length > 0 && doordashReceipt && status === 'online';

  return (
    <div>
      <div className="form-group">
        <label htmlFor="doordashCart">DoorDash Shopping Cart Screenshots *</label>
        <input 
          id="doordashCart" 
          type="file" 
          accept="image/*" 
          multiple
          onChange={handleDoordashCartChange}
          required
          disabled={status === 'offline'}
        />
        <div style={{fontSize: '0.9em', color: '#aaa', marginTop: '4px'}}>
          Upload screenshots of your DoorDash shopping cart showing the items you want to order (you can select multiple cart images if needed)
        </div>
        {doordashCartPreviews.length > 0 && (
          <div style={{marginTop: '12px'}}>
            <div style={{fontSize: '0.9em', color: '#666', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span>Selected {doordashCartPreviews.length} cart screenshot(s):</span>
              {doordashCartPreviews.length > 1 && (
                <button
                  type="button"
                  onClick={clearAllCartImages}
                  style={{
                    background: '#ff6b6b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '0.8em',
                    cursor: 'pointer'
                  }}
                  title="Remove all cart images"
                >
                  Clear All
                </button>
              )}
            </div>
            {doordashCartPreviews.map((preview, index) => (
              <div key={index} style={{position: 'relative', display: 'inline-block', margin: '8px 8px 8px 0'}}>
                <img 
                  src={preview} 
                  alt={`DoorDash cart preview ${index + 1}`} 
                  className="screenshot-preview" 
                  style={{maxWidth: '200px', borderRadius: '8px', maxHeight: '150px', display: 'block'}}
                />
                <button
                  type="button"
                  onClick={() => removeCartImage(index)}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#ff4757',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                  title="Remove this image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="doordashReceipt">DoorDash Receipt Screenshot *</label>
        <input 
          id="doordashReceipt" 
          type="file" 
          accept="image/*" 
          onChange={handleDoordashReceiptChange}
          required
          disabled={status === 'offline'}
        />
        <div style={{fontSize: '0.9em', color: '#aaa', marginTop: '4px'}}>
          Upload a screenshot of your DoorDash receipt showing the final total and order details (one receipt per order)
        </div>
        {doordashReceiptPreview && (
          <div style={{position: 'relative', display: 'inline-block', marginTop: '12px'}}>
            <img 
              src={doordashReceiptPreview} 
              alt="DoorDash receipt preview" 
              className="screenshot-preview" 
              style={{maxWidth: '200px', borderRadius: '8px', maxHeight: '150px', display: 'block'}}
            />
            <button
              type="button"
              onClick={removeReceiptImage}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
              title="Remove this image"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="tip">Tip the Driver</label>
        <input 
          id="tip" 
          value={tip} 
          onChange={e => setTip(e.target.value)} 
          placeholder="$0.00"
          disabled={status === 'offline'}
        />
        <div style={{fontSize: '0.85em', color: '#aaa', marginTop: '4px'}}>
          Tipping the driver increases the chance of faster delivery
        </div>
      </div>

      {/* Subtotal Requirements Notice */}
      <div style={{
        background: 'rgba(255, 224, 130, 0.1)',
        border: '1px solid rgba(255, 224, 130, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          color: '#ffe082',
          fontWeight: '600',
          fontSize: '0.9em',
          marginBottom: '4px'
        }}>
          ⚠️ Order Requirements
        </div>
        <div style={{
          color: '#ccc',
          fontSize: '0.85em',
          lineHeight: '1.4'
        }}>
          Your order subtotal must be between <strong>$15.00</strong> and <strong>$20.00</strong> to be accepted.
        </div>
      </div>

      {loading ? (
        <>
          <style>
            {`
            @keyframes progress-shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(300%); }
            }
            `}
          </style>
          <div style={{
              position: 'relative',
              width: '100%',
              height: '48px',
              backgroundColor: '#333',
              borderRadius: '8px',
              overflow: 'hidden',
              marginTop: '18px'
          }}>
              <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '50%',
                  background: 'linear-gradient(to right, transparent 0%, #ffe082 50%, transparent 100%)',
                  animation: 'progress-shimmer 1.5s infinite'
              }}></div>
          </div>
        </>
      ) : (
        <button 
          onClick={handleOrder} 
          disabled={!isFormValid || status === 'offline'}
          style={{
            background: 'transparent',
            color: (!isFormValid || status === 'offline') ? '#666' : '#fff',
            fontWeight: 600,
            border: (!isFormValid || status === 'offline') ? '1px solid #666' : '1px solid #fff',
            borderRadius: 8,
            padding: '12px 32px',
            fontSize: '1.08em',
            cursor: (!isFormValid || status === 'offline') ? 'not-allowed' : 'pointer',
            width: '100%',
            marginTop: '18px'
          }}
        >
          {status === 'offline' ? 'CLICKYBOIS Offline' : 'Continue to Payment'}
        </button>
      )}

      {/* Custom Popup */}
      {showPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(35, 39, 47, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '14px',
            padding: '32px 28px 28px 28px',
            minWidth: '320px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            position: 'relative',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              color: '#ff4757',
              fontWeight: '600',
              fontSize: '1.2em',
              marginBottom: '16px'
            }}>
              ⚠️ Order Rejected
            </div>
            <div style={{
              color: '#fff',
              fontSize: '1em',
              lineHeight: '1.5',
              marginBottom: '24px',
              whiteSpace: 'pre-line'
            }}>
              {popupMessage}
            </div>
            <button 
              onClick={() => setShowPopup(false)}
              style={{
                background: '#3498db',
                color: '#fff',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '1em',
                cursor: 'pointer',
                minWidth: '100px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 
